// @tennis-matches/ui/tournament-setup/PlayerInput
// Hook for managing player autocomplete with debouncing

import { useState, useCallback, useRef } from 'react';

/**
 * Hook for managing player autocomplete with debouncing
 * @param {Object} config
 * @param {Function} config.onSearch - Async function that takes query and returns Promise<Array>
 * @param {number} config.debounceMs - Debounce delay in milliseconds (default 300)
 * @returns {Object} { suggestions, isSearching, handleSearch, clearSuggestions }
 */
export function usePlayerAutocomplete({ onSearch, debounceMs = 300 }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimer = useRef(null);

  const handleSearch = useCallback(
    (query) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (!query || query.trim().length < 2) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      debounceTimer.current = setTimeout(async () => {
        try {
          if (onSearch && typeof onSearch === 'function') {
            const results = await onSearch(query);
            const suggestionsArray = Array.isArray(results) ? results : [];
            setSuggestions(suggestionsArray);
          } else {
            setSuggestions([]);
          }
        } catch (_) {
          setSuggestions([]);
        } finally {
          setIsSearching(false);
        }
      }, debounceMs);
    },
    [onSearch, debounceMs]
  );

  const clearSuggestions = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSuggestions([]);
    setIsSearching(false);
  }, []);

  return {
    suggestions,
    isSearching,
    handleSearch,
    clearSuggestions
  };
}
